import { useEffect, useState, type JSX } from 'react'
import { formatRelativeTime, formatTimeControl } from '../lib/format.js'
import { roomPath } from '../lib/router.js'
import { signInHref, type Session } from '../lib/session.js'
import { forgetRoom, loadRooms, type RecentRoom } from '../lib/storage.js'
import { StatsPanels, useStats } from './Stats.js'

export interface MyGamesProps {
  readonly session: Session
  readonly navigate: (to: string) => void
}

interface ServerGame {
  readonly key: string
  readonly roomId: string
  readonly playedAt: number
  readonly result: string
  readonly plies: number
  readonly timeControl: string
  readonly white: string
  readonly black: string
  readonly you: 'white' | 'black'
  readonly opening?: string | null
}

/** `1-0` from White's seat is a win; from Black's it is a loss. */
const outcomeOf = (game: ServerGame): { readonly label: string; readonly tone: string } => {
  if (game.result === '1/2-1/2') return { label: 'Draw', tone: 'text-ink-dim' }
  const wonAsWhite = game.result === '1-0' && game.you === 'white'
  const wonAsBlack = game.result === '0-1' && game.you === 'black'
  return wonAsWhite || wonAsBlack
    ? { label: 'Won', tone: 'text-accent' }
    : { label: 'Lost', tone: 'text-danger' }
}

const opponentOf = (game: ServerGame): string => {
  const other = game.you === 'white' ? game.black : game.white
  return other.startsWith('bot:') ? other.slice(4).split('/').pop() ?? 'the computer' : other
}

/**
 * Two sources, one list.
 *
 * Finished games come from the server for a signed-in player; rooms still in
 * progress come from this device's local list, which is also all a guest has.
 * They are shown as two sections rather than merged, because "games you can
 * still play" and "games that are over" are different things to want.
 */
export const MyGames = ({ session, navigate }: MyGamesProps): JSX.Element => {
  const [rooms, setRooms] = useState<readonly RecentRoom[]>(loadRooms)
  const [games, setGames] = useState<readonly ServerGame[] | null>(null)
  const stats = useStats(session.user !== null)

  useEffect(() => {
    if (session.user === null) {
      setGames([])
      return
    }
    let cancelled = false
    void (async (): Promise<void> => {
      try {
        const response = await fetch('/api/me/games', { credentials: 'same-origin' })
        const body = (await response.json()) as { games: ServerGame[] }
        if (!cancelled) setGames(body.games)
      } catch {
        if (!cancelled) setGames([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [session.user])

  return (
    <main className="mx-auto w-full max-w-lg px-4 py-6 safe-top safe-bottom">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label="Back"
          className="rounded px-2 py-1 text-xl leading-none text-ink-dim hover:text-ink"
        >
          ←
        </button>
        <h1 className="text-2xl font-bold">Your games</h1>
      </div>

      {stats !== null && <StatsPanels stats={stats} />}

      {rooms.length > 0 && (
        <section className="mt-6">
          <h2 className="text-xs font-semibold tracking-wide text-ink-dim uppercase">
            Rooms on this device
          </h2>
          <ul className="mt-2 space-y-2">
            {rooms.map((room) => (
              <li key={room.roomId} className="flex items-center gap-2 rounded-panel bg-panel p-3">
                <button
                  type="button"
                  onClick={() => navigate(roomPath(room.roomId, room.key))}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="block truncate text-sm font-semibold">
                    vs {room.opponentName}
                    {room.yourTurn && !room.finished && (
                      <span className="ml-2 rounded bg-accent px-1.5 py-0.5 text-[10px] font-bold text-black">
                        YOUR MOVE
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-xs text-ink-dim">
                    {room.wins}–{room.losses}
                    {room.draws > 0 && ` (${room.draws} drawn)`} · {room.timeControl} ·{' '}
                    {formatRelativeTime(room.updatedAt, Date.now())}
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={`Forget ${room.roomId}`}
                  onClick={() => {
                    forgetRoom(room.roomId)
                    setRooms(loadRooms())
                  }}
                  className="shrink-0 rounded px-2 py-1 text-ink-dim hover:text-ink"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-6">
        <h2 className="text-xs font-semibold tracking-wide text-ink-dim uppercase">
          Finished games
        </h2>

        {session.user === null ? (
          <div className="mt-2 rounded-panel bg-panel p-4">
            <p className="text-sm text-ink-dim">
              Finished games are kept against an account. Sign in and the ones you play from now on
              show up here, on any device.
            </p>
            {session.canSignIn && (
              <a
                href={signInHref()}
                className="mt-3 inline-block rounded bg-panel-raised px-4 py-2 text-sm font-semibold hover:bg-panel-line"
              >
                Sign in with Google
              </a>
            )}
          </div>
        ) : games === null ? (
          <div className="skeleton mt-2 h-20 rounded-panel bg-panel" />
        ) : games.length === 0 ? (
          <p className="mt-2 rounded-panel bg-panel p-4 text-sm text-ink-dim">
            Nothing yet. Finish a game and it will appear here.
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {games.map((game) => {
              const outcome = outcomeOf(game)
              return (
                <li key={game.key}>
                  <button
                    type="button"
                    onClick={() => navigate(roomPath(game.roomId))}
                    className="flex w-full items-center gap-3 rounded-panel bg-panel p-3 text-left hover:bg-panel-raised"
                  >
                    <span className={`w-12 shrink-0 text-sm font-bold ${outcome.tone}`}>
                      {outcome.label}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">
                        vs {opponentOf(game)}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-ink-dim">
                        {game.you === 'white' ? 'White' : 'Black'} · {game.plies} moves ·{' '}
                        {formatTimeControl(game.timeControl)}
                        {game.opening != null && ` · ${game.opening}`} ·{' '}
                        {formatRelativeTime(game.playedAt, Date.now())}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </main>
  )
}
